import React, { useState } from "react";

import Button from "veneer/Button";
import Divider from "veneer/Divider";
import MessageDialog from "veneer/MessageDialog";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import {
  ALL_CONTENT_TYPES,
  STATIC_CONTENT_TYPES,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import { PRIORITY_MAIN } from "cortex/ui/src/fleet/constant/strategy";

import AddSpotModal from "./AddSpotModal";
import SpotTable from "./SpotTable";
import { Spot, Strategy } from "../model";

type StrategyPriority = "main" | "fallback";
type MediaEntryType =
  | "application"
  | "content"
  | "content_group"
  | "folder"
  | "webpage";

interface SpotListProps {
  disabled: boolean;
  priority: StrategyPriority;
  spots: ReadonlyArray<Spot>;
  strategy: Strategy;
}

function getModalTitle(priority: StrategyPriority) {
  return isMainLoop(priority)
    ? "Add existing media, applications, or web pages to your strategy"
    : "Add existing media to your strategy";
}

function getTitle(priority: StrategyPriority) {
  return isMainLoop(priority) ? "Main Loop" : "Fallback Loop";
}

function getButtonHelpText(priority: StrategyPriority) {
  return (
    getModalTitle(priority) + ". To upload new media, go to media library."
  );
}

function getValidContentTypes(priority: StrategyPriority) {
  return (
    isMainLoop(priority) ? ALL_CONTENT_TYPES : STATIC_CONTENT_TYPES
  ) as MediaEntryType[];
}

function isMainLoop(priority: StrategyPriority) {
  return priority === PRIORITY_MAIN.id;
}

export default function SpotList({
  disabled,
  priority,
  spots,
  strategy,
}: SpotListProps) {
  const [showModal, setShowModal] = useState(false);
  const buttonColor = isMainLoop(priority) ? "primary" : "secondary";
  const buttonHelpText = getButtonHelpText(priority);
  const modalTitle = getModalTitle(priority);
  const title = getTitle(priority);
  const validContentTypes = getValidContentTypes(priority);

  return (
    <>
      <Typography color={Colors.gray1000} variant="h4">
        {title}
      </Typography>
      <Divider />
      <Stack spacing={1}>
        <Button
          color={buttonColor}
          disabled={disabled}
          icon="add"
          onClick={() => setShowModal(true)}
        >
          Add to Strategy
        </Button>
        <Typography variant="caption">{buttonHelpText}</Typography>
      </Stack>
      <SpotTable priority={priority} rows={spots} strategy={strategy} />
      <MessageDialog
        dismissLabel="Close"
        onDismiss={() => setShowModal(false)}
        open={showModal}
        title={modalTitle}
        variant="modal-lg"
      >
        <AddSpotModal
          priority={priority}
          validContentTypes={validContentTypes}
        />
      </MessageDialog>
    </>
  );
}
